import chunk from "lodash/chunk";
import isNull from "lodash/isNull";
import last from "lodash/last";
import omitBy from "lodash/omitBy";
import { DateTime } from "luxon";
import React, { useCallback, useMemo } from "react";
import {
    Cell,
    Column,
    FilterAndSort,
    LocalTableSettings,
    Row,
    RowPropGetter,
    SessionTableSettings,
    TableSettings,
    TableState,
} from "react-table";
import { useQueryParam } from "use-query-params";

import { CommaSeparatedNumericArrayParam, CommaSeparatedStringArrayParam } from "../../utils/QueryParameters";
import { KindForTable } from "../../utils/types";
import ActionContainer from "../ActionContainer";
import ProductImage from "../ProductImage";
import { renderCannabisIdCell } from "./cellRenderers";
import { renderILikeFilter } from "./filterRenderers";
import { PricelistTable, isLocalTableSettings } from "./PricelistTable";

// export function initialProductsFilterAndSort(showTasks: boolean, statuses: string[]) {
export function initialKindsFilterAndSort(shopId: string, status: string) {
    const initialFilterBy: any[] = [
        // { id: "status", values: [status] },
        // { id: "shop_id", values: [shopId] },
    ];
    const initialSortBy = [{ id: "name", desc: true }];
    return { filterBy: initialFilterBy, sortBy: initialSortBy };
}

export function initialKindsTableSettings(
    name: string,
    filterAndSort: FilterAndSort,
    hiddenColumns: string[],
    optional?: Partial<TableSettings<KindForTable>>
): TableSettings<KindForTable> {
    const defaults = {
        showAdvancedSearch: false,
        showSettings: false,
        showPaginator: true,
        refresh: false,
        delay: 3000,
        pageSize: 25,
        pageIndex: 0,
        minimized: false,
    };
    let rest = optional ? Object.assign(defaults, optional) : defaults;
    return {
        ...filterAndSort,
        name: name,
        hiddenColumns: hiddenColumns,
        ...rest,
    };
}

interface KindsTableProps {
    initialTableSettings: TableSettings<KindForTable>;
    renderActions: (subscription: KindForTable) => JSX.Element;
}

export function KindsTable({ initialTableSettings, renderActions }: KindsTableProps) {
    const { name } = initialTableSettings;
    const queryNameSpace = last(name.split("."));
    const [pageQ, setPageQ] = useQueryParam(queryNameSpace + "Page", CommaSeparatedNumericArrayParam);
    const [sortQ, setSortQ] = useQueryParam(queryNameSpace + "Sort", CommaSeparatedStringArrayParam);
    const [filterQ, setFilterQ] = useQueryParam(queryNameSpace + "Filter", CommaSeparatedStringArrayParam);

    const initialize = useMemo(
        () =>
            function (current: TableSettings<KindForTable>): TableState<KindForTable> {
                // First get LocalState from LocalStorage
                const settingsFromLocalStorage: LocalTableSettings<KindForTable> | {} = JSON.parse(
                    localStorage.getItem(`table-settings:${current.name}`) || "{}"
                );
                // Then get settings from SessionStorage
                const settingsFromSessionStorage: SessionTableSettings | {} = JSON.parse(
                    sessionStorage.getItem(`table-settings:${current.name}`) || "{}"
                );
                // Then get settings from URL
                let pageIndex: number | null = null;
                let pageSize: number | null = null;
                let sortBy: { id: string; desc: boolean }[] | null = null;
                let filterBy: { id: string; values: string[] }[] | null = null;
                try {
                    if (pageQ) {
                        pageIndex = pageQ[0];
                        pageSize = pageQ[1];
                    }
                    if (sortQ) {
                        sortBy = chunk(sortQ, 2).map(([id, desc]) => ({
                            id,
                            desc: desc === "desc",
                        }));
                    }
                    if (filterQ) {
                        filterBy = chunk(filterQ, 2).map(([id, values]) => ({
                            id: id,
                            values: values ? values.split("-") : [],
                        }));
                    }
                } catch (err) {
                    console.log(err);
                }
                const settingsFromURL = omitBy(
                    {
                        pageIndex: pageIndex,
                        pageSize: pageSize,
                        sortBy: sortBy,
                        filterBy: filterBy,
                    },
                    isNull
                );
                // merge everything and return as new table state. Each object from left to right can override keys from the previous object.
                return Object.assign(
                    { loading: true, pageCount: 0, data: [] },
                    current,
                    settingsFromLocalStorage,
                    settingsFromSessionStorage,
                    settingsFromURL
                );
            },
        [filterQ, pageQ, sortQ]
    );

    const extraRowPropGetter: RowPropGetter<KindForTable> = useCallback((props, { row }) => {
        return {
            ...props,
            id: row.values.subscription_id,
            className: `${row.values.status}`,
        };
    }, []);

    const renderSubComponent = useCallback(({ row }: { row: Row<KindForTable> }) => {
        return <b>Yo</b>;
    }, []);

    const initialState = useMemo(() => initialize(initialTableSettings), [initialTableSettings, initialize]);
    const columns: Column<KindForTable>[] = useMemo(
        () =>
            [
                {
                    Header: "id",
                    accessor: "id",
                    Filter: renderILikeFilter,
                    Cell: renderCannabisIdCell,
                },
                {
                    Header: "Naam",
                    id: "name",
                    accessor: "name",
                    Filter: renderILikeFilter,
                },
                {
                    Header: "Beschrijving NL",
                    id: "short_description_nl",
                    accessor: "short_description_nl",
                    Filter: renderILikeFilter,
                },
                {
                    Header: "Beschrijving EN",
                    id: "short_description_en",
                    accessor: "short_description_en",
                    Filter: renderILikeFilter,
                },
                {
                    Header: "Beschr. lang NL",
                    id: "description_nl",
                    accessor: "description_nl",
                    Filter: renderILikeFilter,
                },
                {
                    Header: "Beschr. lang EN",
                    id: "description_en",
                    accessor: "description_en",
                    Filter: renderILikeFilter,
                },
                {
                    Header: "Created",
                    id: "created_at",
                    accessor: (d: { created_at: string }) =>
                        DateTime.fromISO(d.created_at)
                            .plus({ hours: 2 })
                            .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
                    disableFilters: true,
                },
                {
                    Header: "Modified at",
                    id: "modified_at",
                    accessor: (d: { modified_at: string }) =>
                        DateTime.fromISO(d.modified_at)
                            .plus({ hours: 2 })
                            .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
                    disableFilters: true,
                },
                {
                    Header: "Image",
                    id: "image_1",
                    accessor: (d: { image_1: string }) => <ProductImage imageKey={d.image_1} width={100} />,
                    disableFilters: true,
                },
                {
                    Header: "Flags",
                    id: "flags",
                    accessor: (d: { flags: string[] }) =>
                        d.flags?.map((flag: string) => <span style={{ fontSize: 26, marginRight: 2 }}>{flag}</span>),
                    disableFilters: true,
                },
                {
                    Header: "Extra Icons",
                    id: "icons",
                    accessor: (d: { icons: string[] }) =>
                        d.icons?.map((icon: string) => <span style={{ fontSize: 26, marginRight: 2 }}>{icon}</span>),
                    disableFilters: true,
                },
                {
                    Header: "",
                    id: "actions",
                    accessor: (originalRow: KindForTable, index: number) => originalRow,
                    Cell: ({ cell }: { cell: Cell }) => (
                        <ActionContainer
                            title={"Actions"}
                            renderButtonContent={(active) => {
                                const classes = ["dropdown-button-content", active ? "active" : ""].join(" ");
                                return (
                                    <span className={classes}>
                                        <i className={"fa fa-bars"} />
                                    </span>
                                );
                            }}
                            renderContent={(disabled) => renderActions(cell.value)}
                        />
                    ),
                    disableFilters: true,
                    disableSortBy: true,
                },
            ] as Column<KindForTable>[],
        [renderActions]
    );

    const persistSettings = useCallback(
        (settings: LocalTableSettings<KindForTable> | SessionTableSettings) => {
            if (isLocalTableSettings(settings)) {
                localStorage.setItem(`table-settings:${name}`, JSON.stringify(settings));
            } else {
                sessionStorage.setItem(`table-settings:${name}`, JSON.stringify(settings));
                setPageQ([settings.pageIndex, settings.pageSize]);
                setSortQ(settings.sortBy.map(({ id, desc }) => [id, desc ? "desc" : "asc"]).flat());
                setFilterQ(settings.filterBy.map(({ id, values }) => [id, values.join("-")]).flat());
            }
        },
        [name, setFilterQ, setPageQ, setSortQ]
    );

    return (
        <div key={name}>
            <section className="kinds-table" id={`${name}-KindForTable`}>
                <PricelistTable<KindForTable>
                    columns={columns}
                    initialState={initialState as TableState<KindForTable>}
                    persistSettings={persistSettings}
                    endpoint={"kinds"}
                    initialTableSettings={initialTableSettings}
                    extraRowPropGetter={extraRowPropGetter}
                    renderSubComponent={renderSubComponent}
                    excludeInFilter={["shop_id"]}
                    advancedSearch={true} /* Dream on? Or might be easy possible with algol! */
                />
            </section>
        </div>
    );
}
